import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { toast } from 'sonner';
import { useErrorMessages, useTextMessaging } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { useConversationDeleteMutation } from '../../../../../queries/Conversation/useConversationDeleteMutation';
import { useUnmarkConversationAsSpamMutation } from '../../../../../queries/Conversation/useUnmarkConversationAsSpamMutation';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { ConversationItem } from '../../ConversationItem';
import { useTextMessageConversationsContext } from '../context';
import { styleSheet } from '../styles';

interface Props {
	unreadOnly: boolean;
	spamOnly: boolean;
}

export const ConversationList = observer(({ unreadOnly, spamOnly }: Props) => {
	const textMessagingVM = useTextMessaging();
	const {
		selectedConversation,
		selectedRecipient,
		setRecipients,
		setSelectedConversation,
		setSelectedRecipient,
		setSelectedRecipientIndex,
		setShowNewConversationSearch,
	} = useTextMessageConversationsContext();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging();
	const history = useHistory();
	const conversationDeleteMutation = useConversationDeleteMutation({
		onError: error => {
			logger.logApiError('ArchiveTextConversation-Error', error);
			toast.error(error.systemMessage || 'Failed to archive conversation');
		},
		onSuccess: (_, { id }) => {
			textMessagingVM.onArchiveConversation(id);
			toast.success('Conversation deleted.');
			if (selectedConversation?.id === id) {
				setSelectedConversation(null);

				setSelectedRecipientIndex(null);
				history.push('/texting');
			}
		},
	});

	const unmarkSpamMutation = useUnmarkConversationAsSpamMutation({
		onError: error => {
			logger.logApiError('UnmarkAsSpamConversation-Error', error);
			errorMessages.pushApiError(Api.asApiError(error));
		},
		onSuccess: (_, { id }) => {
			textMessagingVM.removeConversationById(id);
			if (selectedConversation?.id === id) {
				setSelectedConversation(null);
				setSelectedRecipient(null);
				setRecipients([]);
				setSelectedRecipientIndex(null);
				history.push('/texting');
			}
			toast.success('Conversation restored to inbox.');
		},
	});

	const onConversationClick = (conversation: Api.ConversationViewModel) => {
		setShowNewConversationSearch(false);
		if (selectedConversation?.id !== conversation.id) {
			setRecipients([]);

			history.push(`/texting/${textMessagingVM.id}/${conversation.id}`);
		}
	};

	const onTrashConversationClick = (conversation: Api.ConversationViewModel) => {
		conversationDeleteMutation.mutate({ id: conversation.id });
	};

	const onMarkConversationAsSpamClick = async (conversation: Api.ConversationViewModel) => {
		try {
			await textMessagingVM.markAsSpam(conversation.id);
			if (selectedConversation?.id === conversation.id) {
				setSelectedConversation(null);
				setSelectedRecipient(null);
				// Also clear the recipients array; otherwise the recipients-sync effect in
				// Conversations/index.tsx rehydrates selectedRecipient from it, leaving the
				// contact drawer open after the conversation was marked spam.
				setRecipients([]);
				setSelectedRecipientIndex(null);
				history.push('/texting');
			}

			toast.success('Conversation marked as spam.');
		} catch (err) {
			logger.logApiError('MarkAsSpamConversation-Error', err);

			errorMessages.pushApiError(Api.asApiError(err));
		}
	};

	const onUnmarkConversationAsSpamClick = (conversation: Api.ConversationViewModel) => {
		if (unmarkSpamMutation.isPending) {
			return;
		}
		unmarkSpamMutation.mutate({ id: conversation.id });
	};

	const onUnsubscribeConversationClick = async (conversation: Api.ConversationViewModel) => {
		// create an unsubscribed variable (and pass to conversation item) based on the existance of automatedSMSOptOutDate
		// if it exists on the conversation then the conversation is unsubscribed.
		try {
			await textMessagingVM.toggleUnsubscribeConversation(conversation);

			toast.success(`Conversation ${conversation.automatedSMSOptOutDate ? 'un' : ''}subscribed.`);
		} catch (err) {
			logger.logApiError('UnsubscribedConvo-Error', err);

			errorMessages.pushApiError(Api.asApiError(err));
		}
	};

	const onMarkConversationAsUnreadClick = async (conversation: Api.ConversationViewModel) => {
		try {
			await conversation.markAsUnread();
			toast.success('Conversation marked as unread.');
		} catch (err) {
			logger.logApiError('MarkAsUnreadConversation-Error', err);

			errorMessages.pushApiError(Api.asApiError(err));
		}
	};

	const loadMoreConversations = () => {
		textMessagingVM
			.getConversations({ unreadOnly, spamOnly })
			.catch(err => errorMessages.pushApiError(Api.asApiError(err)));
	};

	const isLoading = textMessagingVM.loadingConversations && !textMessagingVM.conversations?.length;

	const allLoaded = !textMessagingVM.allConversationsLoaded;

	const noConversations =
		textMessagingVM.conversationsLoaded &&
		!textMessagingVM.loadingConversations &&
		textMessagingVM.conversations.length === 0;

	const filterByLast = (conversation: Api.ConversationViewModel) => conversation.lastMessage;

	return (
		<div className={css(styleSheet.conversationsContainer)}>
			{noConversations && (
				<div className={css(styleSheet.noConversations)}>{spamOnly ? 'No spam conversations' : 'No conversations'}</div>
			)}

			{textMessagingVM.conversations.filter(filterByLast).map(conversation => (
				<ConversationItem
					className={css(styleSheet.conversationItem)}
					conversation={conversation}
					isSpamList={spamOnly}
					key={conversation.id}
					onClick={onConversationClick}
					onMarkAsSpamClick={onMarkConversationAsSpamClick}
					onMarkAsUnreadClick={onMarkConversationAsUnreadClick}
					onTrashClick={onTrashConversationClick}
					onUnmarkAsSpamClick={onUnmarkConversationAsSpamClick}
					onUnsubscribeClick={onUnsubscribeConversationClick}
					selected={selectedConversation?.id === conversation.id}
					selectedRecipient={selectedRecipient}
				/>
			))}
			{allLoaded && <Waypoint key='conversations-waypoint' bottomOffset='-200px' onEnter={loadMoreConversations} />}
			{isLoading && <LoadingSpinner className={css(styleSheet.conversationsLoading)} />}
		</div>
	);
});
